import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { RouterModule, Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { ToastModule } from 'primeng/toast';
import { Toast } from 'primeng/toast';
import { ThemeService } from './app/services/theme.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from '@/services/auth.service';
import { PermissionService } from '@/services/permission.service';
import { LayoutService } from '@/layout/service/layout.service';
import { UserPreferenceService } from '@/services/user-preference.service';
import { NotificationLiveService } from '@/services/notification-live.service';
import { CollabLiveService } from '@/services/collab-live.service';
import { NotificationToastHostComponent } from '@/shared/notification-toast/notification-toast-host.component';

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [RouterModule, ToastModule, Toast, NotificationToastHostComponent],
    templateUrl: 'app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
    private translate = inject(TranslateService);
    private router = inject(Router);
    private activatedRoute = inject(ActivatedRoute);
    private titleService = inject(Title);
    private themeService = inject(ThemeService);

    private sub = new Subscription();
    private siteTitle: string = '';
    private checkTokenInterval: any;
    private loggedIn = false;

    constructor(
        private config: PrimeNG,
        private authService: AuthService,
        private permissionService: PermissionService,
        private userPreferenceService: UserPreferenceService,
        private notificationLiveService: NotificationLiveService,
        private collabLiveService: CollabLiveService,
        _layoutService: LayoutService
    ) {
        this.translate.addLangs(['de']);
        this.translate.setFallbackLang('de');

        this.translate.get('primeng').subscribe((res) => this.config.setTranslation(res));
        // site title from theme
        this.themeService.themeData.subscribe({ next: data => { this.siteTitle = data?.theme_title || ''; } });

        // update title on navigation
        this.sub.add(
            this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => this.updateTitle())
        );
        // re-update title once translations are loaded (fixes race with HTTP loader)
        this.sub.add(
            this.translate.onTranslationChange.subscribe(() => this.updateTitle())
        );
    }

    ngOnInit(): void {
        this.notificationLiveService.watchSession();
        this.collabLiveService.watchSession();

        this.authService.sessionData.subscribe({
            next: async (data) => {
                this.loggedIn = data.isLoggedIn;

                if (!this.loggedIn) {
                    this.userPreferenceService.reset();
                    if (this.checkTokenInterval) {
                        clearInterval(this.checkTokenInterval);
                        this.checkTokenInterval = undefined;
                    }
                    return;
                }

                await this.userPreferenceService.ensureLoaded(data.user?._id);

                if (!this.checkTokenInterval) {
                    this.authService.checkAndRenewToken();
                    this.checkTokenInterval = setInterval(
                        () => {
                            this.authService.checkAndRenewToken();
                        },
                        1000 * 60 * 2,
                    );
                }
            } });

    }

    ngOnDestroy() {
        this.notificationLiveService.disconnect();
        this.collabLiveService.disconnect();
        this.sub.unsubscribe();
    }

    private updateTitle() {
        // find deepest route
        let route = this.activatedRoute.firstChild;
        while (route?.firstChild) {
            route = route.firstChild;
        }

        const snapshot = route?.snapshot;
        let pageTitle = '';

        // compute URL once
        const fullUrl = this.router.url || '';

        if (snapshot?.data && snapshot.data['title']) {
            pageTitle = snapshot.data['title'];
            // detect admin area: route data can set area: 'admin' or fallback to URL check
            const isPlatform =
                snapshot.data['area'] === 'platform' ||
                fullUrl.startsWith('/app/roles') ||
                fullUrl.startsWith('/app/permissions') ||
                fullUrl.startsWith('/app/platform-settings') ||
                fullUrl.startsWith('/app/users');

            // try translate
            this.translate.get(pageTitle).subscribe(trans => {
                if (isPlatform) {
                    const title = `Platform - ${trans || pageTitle}`;
                    this.titleService.setTitle(title);
                } else {
                    const title = `${trans || pageTitle} - ${this.siteTitle || 'CMP'}`;
                    this.titleService.setTitle(title);
                }
            });
            return;
        }

        // fallback: last path segment
        const url = this.router.url || '';
        const parts = url.split('/').filter(p => p && !p.startsWith('?'));
        if (parts.length === 0) {
            pageTitle = this.siteTitle || 'CMP';
        } else {
            const last = parts[parts.length - 1];
            pageTitle = last.replace(/-/g, ' ');
            pageTitle = pageTitle.charAt(0).toUpperCase() + pageTitle.slice(1);
        }
    const isPlatform =
        fullUrl.startsWith('/app/users') ||
        fullUrl.startsWith('/app/roles') ||
        fullUrl.startsWith('/app/permissions') ||
        fullUrl.startsWith('/app/platform-settings') ||
        fullUrl.startsWith('/app/users');
    const title = isPlatform ? `Platform - ${pageTitle}` : `${pageTitle} - ${this.siteTitle || 'CMP'}`;
        this.titleService.setTitle(title);
    }

}
